import { HrTime } from '@opentelemetry/api';
import { flatbuffers } from 'flatbuffers';
import { TypesFB } from './Protocol/EndeavourProtocol';
import { Attributes } from './Types';

const supportsNativeBigInt = typeof BigInt === 'function';

export class ProtocolUtils {
  public static createStringKeyAnyValueIdx(builder: flatbuffers.Builder, key: string, value: number | string | boolean | unknown): number {
    if (typeof value === 'string') {
      return TypesFB.StringKeyAnyValue.createStringKeyAnyValue(
        builder,
        builder.createString(key),
        TypesFB.AnyValue.StringValue,
        TypesFB.StringValue.createStringValue(builder, builder.createString(value))
      );
    } else if (typeof value === 'number') {
      return TypesFB.StringKeyAnyValue.createStringKeyAnyValue(
        builder,
        builder.createString(key.toString()),
        TypesFB.AnyValue.DoubleValue,
        TypesFB.DoubleValue.createDoubleValue(builder, value)
      );
    } else if (typeof value === 'boolean') {
      // boolean
      return TypesFB.StringKeyAnyValue.createStringKeyAnyValue(
        builder,
        builder.createString(key.toString()),
        TypesFB.AnyValue.BooleanValue,
        TypesFB.BooleanValue.createBooleanValue(builder, value)
      );
    } else {
      return 0;
    }
  }

  public static createStandardKeyAnyValueIdx(
    builder: flatbuffers.Builder,
    key: string,
    value: number | string | boolean | unknown
  ): number {
    const stdAttrKey = ProtocolUtils.getStandardAttributeName(key);
    if (stdAttrKey !== TypesFB.StandardAttributeKey.NO_KEY) {
      if (typeof value === 'string') {
        return TypesFB.StandardKeyAnyValue.createStandardKeyAnyValue(
          builder,
          stdAttrKey,
          TypesFB.AnyValue.StringValue,
          TypesFB.StringValue.createStringValue(builder, builder.createString(value))
        );
      } else if (typeof value === 'number') {
        return TypesFB.StandardKeyAnyValue.createStandardKeyAnyValue(
          builder,
          stdAttrKey,
          TypesFB.AnyValue.DoubleValue,
          TypesFB.DoubleValue.createDoubleValue(builder, value)
        );
      } else if (typeof value === 'boolean') {
        // boolean
        return TypesFB.StandardKeyAnyValue.createStandardKeyAnyValue(
          builder,
          stdAttrKey,
          TypesFB.AnyValue.BooleanValue,
          TypesFB.BooleanValue.createBooleanValue(builder, value)
        );
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }

  public static createCustomAttributeIndexes(builder: flatbuffers.Builder, attributes: Attributes | undefined): number[] {
    const AttributeIndexes: Array<flatbuffers.Offset> = [];
    if (attributes !== undefined) {
      Object.keys(attributes).forEach((key) => AttributeIndexes.push(this.createStringKeyAnyValueIdx(builder, key, attributes[key])));
    }
    return AttributeIndexes;
  }

  public static createStandardAttributeIndexes(builder: flatbuffers.Builder, attributes: Attributes | undefined): number[] {
    const AttributeIndexes: Array<flatbuffers.Offset> = [];
    if (attributes !== undefined) {
      Object.keys(attributes).forEach((key) => {
        let attrValueIdx = this.createStandardKeyAnyValueIdx(builder, key, attributes[key]);
        if (attrValueIdx !== 0) {
          AttributeIndexes.push(attrValueIdx);
        }
      });
    }
    return AttributeIndexes;
  }

  private static getStandardAttributeName(key: string): TypesFB.StandardAttributeKey {
    switch (key) {
      case 'os_type': {
        return TypesFB.StandardAttributeKey.OS_TYPE;
      }
      case 'os_version': {
        return TypesFB.StandardAttributeKey.OS_VERSION;
      }
      case 'machine_id': {
        return TypesFB.StandardAttributeKey.MACHINE_ID;
      }
      case 'process_id': {
        return TypesFB.StandardAttributeKey.PROCESS_ID;
      }
      case 'product_name': {
        return TypesFB.StandardAttributeKey.PRODUCT_NAME;
      }
      case 'product_version': {
        return TypesFB.StandardAttributeKey.PRODUCT_VERSION;
      }
      case 'event_id': {
        return TypesFB.StandardAttributeKey.EVENT_ID;
      }
      case 'workbook_id': {
        return TypesFB.StandardAttributeKey.WORKBOOK_ID;
      }
      case 'site_id': {
        return TypesFB.StandardAttributeKey.SITE_ID;
      }
      case 'site_luid': {
        return TypesFB.StandardAttributeKey.SITE_LUID;
      }
      case 'user_luid': {
        return TypesFB.StandardAttributeKey.USER_LUID;
      }
      case 'domain_id': {
        return TypesFB.StandardAttributeKey.DOMAIN_ID;
      }
      case 'site_role_id': {
        return TypesFB.StandardAttributeKey.SITE_ROLE_ID;
      }
      case 'system_user_id': {
        return TypesFB.StandardAttributeKey.SYSTEM_USER_ID;
      }
      case 'online_pod_uuid': {
        return TypesFB.StandardAttributeKey.ONLINE_POD_UUID;
      }
      case 'tsm_cluster_id': {
        return TypesFB.StandardAttributeKey.TSM_CLUSTER_ID;
      }
      case 'client_library_name': {
        return TypesFB.StandardAttributeKey.CLIENT_LIB_NAME;
      }
      case 'user_name': {
        return TypesFB.StandardAttributeKey.USER_NAME;
      }
      case 'session_id': {
        return TypesFB.StandardAttributeKey.SESSION_ID;
      }
      case 'datasource_id': {
        return TypesFB.StandardAttributeKey.DATASOURCE_ID;
      }
      case 'atr_chain_id': {
        return TypesFB.StandardAttributeKey.ATR_CHAIN_IDS;
      }
      case 'fulfillment_ids': {
        return TypesFB.StandardAttributeKey.FULFILLMENT_IDS;
      }
      case 'service_name': {
        return TypesFB.StandardAttributeKey.SERVICE_NAME;
      }
      default: {
        return TypesFB.StandardAttributeKey.NO_KEY;
      }
    }
  }

  // from https://github.com/google/flatbuffers/pull/4232
  public static numberToLong(value: Number): flatbuffers.Long {
    let padded = value.toString(2).padStart(64, '0');
    return flatbuffers.Long.create(parseInt(padded.substring(32), 2), parseInt(padded.substring(0, 32), 2));
  }

  // from https://github.com/google/flatbuffers/pull/4232
  public static buildTimeStamp(timestamp: HrTime): flatbuffers.Long {
    let timeValue = undefined;
    if (supportsNativeBigInt) {
      timeValue = BigInt(Math.round(timestamp[0]) + '' + Math.round(timestamp[1]).toString().padEnd(9, '0'));
    } else {
      timeValue = Number(0);
    }
    const padded = timeValue.toString(2).padStart(64, '0');
    return flatbuffers.Long.create(parseInt(padded.substring(32), 2), parseInt(padded.substring(0, 32), 2));
  }

  /**
   * Support bigInt serialization from a hex string (i.e. traceIds and spanIDs) into a Uint8Array
   * used by the telemetry server.
   *  Reference: https://coolaj86.com/articles/convert-js-bigints-to-typedarrays/
   */
  public static hexIdToBuf(hexId: string): Uint8Array {
    if (hexId.length % 2) {
      hexId = '0' + hexId;
    }
    const len = hexId.length / 2;
    const u8 = new Uint8Array(len);

    let i = 0;
    let j = 0;
    while (i < len) {
      u8[i] = parseInt(hexId.slice(j, j + 2), 16);
      i += 1;
      j += 2;
    }

    return u8;
  }
}
